import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../services/adminApi';
import { SUPPORTED_GROUP_SIZES_LABEL } from '../constants/leagueGroupSizes';

const Container = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Title = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ActionButton = styled.button`
  background: ${props => {
    if (props.disabled || props.$isLoading) return '#9ca3af';
    if (props.$variant === 'primary') return '#3b82f6';
    if (props.$variant === 'success') return '#16a34a';
    if (props.$variant === 'danger') return '#dc2626';
    return '#6b7280';
  }};
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: ${props => (props.$isLoading || props.disabled) ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  min-height: 80px;
  justify-content: center;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonIcon = styled.div`
  font-size: 1.5rem;
`;

const ButtonText = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
`;

const ButtonDescription = styled.div`
  font-size: 0.75rem;
  opacity: 0.9;
  margin-top: 0.25rem;
`;

const MessageContainer = styled.div`
  background: ${props => {
    if (props.$type === 'success') return '#dcfce7';
    if (props.$type === 'error') return '#fee2e2';
    if (props.$type === 'warning') return '#fef3c7';
    return '#e0f2fe';
  }};
  border: 1px solid ${props => {
    if (props.$type === 'success') return '#16a34a';
    if (props.$type === 'error') return '#dc2626';
    if (props.$type === 'warning') return '#d97706';
    return '#0284c7';
  }};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: ${props => {
    if (props.$type === 'success') return '#166534';
    if (props.$type === 'error') return '#991b1b';
    if (props.$type === 'warning') return '#92400e';
    return '#0c4a6e';
  }};
`;

const InfoSection = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 1rem;
  font-size: 0.875rem;
  color: #374151;
  border: 1px solid #e5e7eb;
`;

const SimpleMatchGenerationControl = ({ selectedSeason, readOnly = false }) => {
  const [message, setMessage] = useState(null);
  const [showClearWarning, setShowClearWarning] = useState(false);
  const [clearPreview, setClearPreview] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');
  const queryClient = useQueryClient();

  // Check if matches exist
  const { data: matchesData } = useQuery(
    ['admin-matches', selectedSeason],
    () => adminAPI.get(`/admin/matches?season_id=${selectedSeason}`),
    {
      enabled: !!selectedSeason,
      retry: false
    }
  );

  // Division status data - only fetched when needed (not auto-loaded)
  const [divisionStatusData, setDivisionStatusData] = useState(null);

  // Handle both response formats: { data: { matches: [...] } } and { data: [...] }
  const matchesArray = matchesData?.data?.matches || matchesData?.data || [];
  const hasMatches = matchesArray.length > 0;

  // Generate matches mutation (using existing endpoint)
  const generateMatchesMutation = useMutation(
    async () => {
      // Optionally fetch division status (do not block generation if this fails)
      try {
        const statusResponse = await adminAPI.get(`/match-generation/admin/seasons/${selectedSeason}/match-status`);
        setDivisionStatusData(statusResponse.data);
      } catch (statusErr) {
        console.warn('Match status prefetch failed (generation will still run):', statusErr?.message);
      }
      // Always run match generation
      return await adminAPI.post(`/match-generation/admin/seasons/${selectedSeason}/generate-all-matches`);
    },
    {
      onSuccess: (response) => {
        // Admin API interceptor returns the unwrapped body, so response IS the payload (not response.data)
        const responseData = response?.data ?? response ?? {};
        const diagnostic = responseData.diagnostic;
        const results = responseData.results || diagnostic?.results || [];
        
        console.log('🔍 Full match generation response:', response);
        console.log('🔍 Response data:', responseData);
        console.log('🔍 Diagnostic data:', diagnostic);
        console.log('🔍 Results array:', results);
        
        // Log detailed diagnostic information to browser console
        if (diagnostic) {
          console.log('%c📊 MATCH GENERATION DIAGNOSTIC REPORT', 'font-size: 16px; font-weight: bold; color: #2563eb;');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          
          console.log(`%c📈 OVERVIEW`, 'font-size: 14px; font-weight: bold; color: #059669;');
          console.log(`   Total Teams: ${diagnostic.totalTeams}`);
          console.log(`   Expected Matches: ${diagnostic.expectedMatches} (${diagnostic.totalTeams} teams × 6 matches ÷ 2)`);
          console.log(`   Matches Generated: ${diagnostic.totalMatchesGenerated}`);
          console.log(`   Missing Matches: ${diagnostic.missingMatches}`);
          console.log(`   Actual in DB: ${diagnostic.actualMatchesInDB}`);
          
          console.log(`\n%c📋 DIVISION BREAKDOWN`, 'font-size: 14px; font-weight: bold; color: #059669;');
          console.log(`   Total Divisions Found: ${diagnostic.allDivisions.length}`);
          console.log(`   Divisions to Process: ${diagnostic.totalDivisions}`);
          console.log(`   ✅ Successful: ${diagnostic.successfulDivisions}`);
          console.log(`   ⚠️  Skipped: ${diagnostic.skippedDivisions}`);
          console.log(`   ❌ Failed: ${diagnostic.failedDivisions}`);
          
          if (diagnostic.skippedDivisions && diagnostic.skippedDivisions.length > 0) {
            console.log(`\n%c⚠️  SKIPPED DIVISIONS (${diagnostic.skippedDivisions.length})`, 'font-size: 14px; font-weight: bold; color: #dc2626;');
            diagnostic.skippedDivisions.forEach(skip => {
              console.log(`   • ${skip.name}: ${skip.teamCount} teams - ${skip.reason}`);
              if (skip.teams && skip.teams.length > 0) {
                console.log(`     Teams: ${skip.teams.map(t => t.name).join(', ')}`);
              }
            });
          }
          
          console.log(`\n%c📊 ALL DIVISIONS`, 'font-size: 14px; font-weight: bold; color: #059669;');
          diagnostic.allDivisions.forEach(div => {
            const status = div.willProcess 
              ? (div.isSupported ? '✅ WILL PROCESS' : '⚠️  WILL SKIP (unsupported team count)')
              : '❌ EXCLUDED';
            const statusColor = div.willProcess && div.isSupported ? '#059669' : '#dc2626';
            console.log(`%c   ${status}`, `color: ${statusColor}; font-weight: bold;`);
            console.log(`      ${div.name} (${div.teamCount} teams) [${div.isGroupDivision ? 'GROUP' : 'REGULAR'}]`);
            if (div.teams && div.teams.length > 0) {
              console.log(`      Teams: ${div.teams.map(t => t.name).join(', ')}`);
            }
          });
          
          if (diagnostic.results && diagnostic.results.length > 0) {
            console.log(`\n%c✅ PROCESSING RESULTS`, 'font-size: 14px; font-weight: bold; color: #059669;');
            diagnostic.results.forEach((result, index) => {
              if (result.success) {
                console.log(`%c   ✅ ${result.divisionName}`, 'color: #059669;');
                console.log(`      Generated ${result.matchesGenerated} matches for ${result.teams} teams`);
              } else {
                console.log(`%c   ❌ ${result.divisionName}`, 'color: #dc2626;');
                console.log(`      ${result.error || result.message || 'Failed'}`);
              }
            });
          }
          
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('%c📊 END OF DIAGNOSTIC REPORT', 'font-size: 16px; font-weight: bold; color: #2563eb;');
        } else {
          // If diagnostic data is missing, log a summary from the response
          console.log('%c📊 MATCH GENERATION SUMMARY', 'font-size: 16px; font-weight: bold; color: #2563eb;');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          
          console.log(`%c📈 OVERVIEW`, 'font-size: 14px; font-weight: bold; color: #059669;');
          console.log(`   Total Teams Processed: ${responseData.totalTeamsProcessed || 0}`);
          console.log(`   Expected Matches: ${responseData.expectedMatches || 0} (${responseData.totalTeamsProcessed || 0} teams × 6 matches ÷ 2)`);
          console.log(`   Matches Generated: ${responseData.totalMatchesGenerated || 0}`);
          console.log(`   Missing Matches: ${(responseData.expectedMatches || 0) - (responseData.totalMatchesGenerated || 0)}`);
          console.log(`   Actual in DB: ${responseData.actualMatchesInDB || 0}`);
          
          console.log(`\n%c📋 DIVISION BREAKDOWN`, 'font-size: 14px; font-weight: bold; color: #059669;');
          console.log(`   Total Divisions: ${responseData.totalDivisions || 0}`);
          console.log(`   ✅ Successful: ${responseData.successfulDivisions || 0}`);
          console.log(`   ⚠️  Skipped: ${responseData.skippedDivisions || 0}`);
          console.log(`   ❌ Failed: ${responseData.failedDivisions || 0}`);
          
          if (responseData.skippedDivisions > 0) {
            console.log(`\n%c⚠️  ${responseData.skippedDivisions} divisions were skipped`, 'font-size: 14px; font-weight: bold; color: #dc2626;');
            console.log('   Check server logs for details on which divisions were skipped');
          }
          
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('%c⚠️  Full diagnostic data not available - check server logs for details', 'font-size: 12px; color: #dc2626;');
        }
        
        // Check if we have detailed results
        if (results && results.length > 0) {
          const succeeded = results.filter(r => r.success && (r.matchesGenerated || 0) > 0);
          const failed = results.filter(r => !r.success && r.error); // exception thrown
          const skipped = results.filter(r => !r.success && !r.error); // e.g. unsupported team count (4, 6, 8, 10 only)
          
          const totalMatchesGenerated = succeeded.reduce((sum, div) => sum + (div.matchesGenerated || 0), 0);
          let messageText = '';
          let messageType = 'success';
          
          if (succeeded.length > 0) {
            messageText += `✅ Generated matches for ${succeeded.length} division(s) (${totalMatchesGenerated} total matches):\n\n`;
            succeeded.forEach(div => {
              messageText += `   ✓ ${div.divisionName || div.divisionId}: ${div.matchesGenerated} matches\n`;
            });
          }
          
          if (skipped.length > 0) {
            messageText += succeeded.length > 0 ? '\n' : '';
            messageType = succeeded.length > 0 ? 'warning' : 'warning';
            messageText += `⚠️ Skipped (not supported – need ${SUPPORTED_GROUP_SIZES_LABEL} teams): ${skipped.length} division(s)\n\n`;
            skipped.forEach(div => {
              const reason = (div.message || '').replace(/\n\n+/g, ' ').replace(/⚠️\s*/g, '').trim();
              messageText += `   • ${div.divisionName || div.divisionId}: ${reason}\n`;
            });
          }
          
          if (failed.length > 0) {
            messageText += (succeeded.length > 0 || skipped.length > 0) ? '\n' : '';
            messageType = succeeded.length === 0 ? 'error' : 'warning';
            messageText += `❌ Failed (error) for ${failed.length} division(s):\n\n`;
            failed.forEach(div => {
              const err = (div.error || div.message || 'Unknown error').replace(/\n\n+/g, ' ').trim();
              messageText += `   ✗ ${div.divisionName || div.divisionId}: ${err}\n`;
            });
          }
          
          if (succeeded.length === 0 && skipped.length === 0 && failed.length === 0) {
            messageText = `✅ ${responseData?.message || 'Match generation completed.'}`;
            messageType = 'success';
          } else if (succeeded.length === 0 && failed.length > 0) {
            messageText += `\n💡 Ensure divisions have ${SUPPORTED_GROUP_SIZES_LABEL} teams and all teams are assigned to divisions.`;
          }
          
          setMessage({
            type: messageType,
            text: messageText
          });
        } else if (responseData?.success || responseData?.message || response.data?.success) {
          // If no detailed results but API call succeeded, check if message indicates failure
          const message = responseData?.message || response.data?.message || '';
          if (message.toLowerCase().includes('processed') && !message.toLowerCase().includes('generated')) {
            setMessage({
              type: 'error',
              text: `❌ Match generation failed: No divisions were ready for match generation. Please ensure divisions have ${SUPPORTED_GROUP_SIZES_LABEL} teams.`
            });
          } else {
            setMessage({
              type: 'success',
              text: `✅ ${message || 'Matches generated successfully!'}`
            });
          }
        } else {
          // If no detailed results and no success indicator, show error
          setMessage({
            type: 'error',
            text: '❌ Match generation failed: No results returned. Please check division team counts.'
          });
        }
        
        queryClient.invalidateQueries(['admin-matches']);
        queryClient.invalidateQueries(['divisions-overview']);
      },
      onError: (error) => {
        setMessage({
          type: 'error',
          text: `❌ Failed to generate matches: ${error.response?.data?.message || error.message}`
        });
      }
    }
  );

  // Get clear matches preview
  const getClearPreview = async () => {
    try {
      const response = await adminAPI.get(`/clear-matches/admin/seasons/${selectedSeason}/clear-matches-preview`);
      const previewData = response.data?.data || response.data;
      setClearPreview(previewData);
    } catch (error) {
      console.error('❌ Failed to get clear preview:', error);
      setMessage({
        type: 'error',
        text: `❌ Failed to get clear preview: ${error.response?.data?.message || error.message}`
      });
    }
  };

  // Clear all matches mutation
  const clearMatchesMutation = useMutation(
    () => adminAPI.post(`/clear-matches/admin/seasons/${selectedSeason}/clear-all-matches`),
    {
      onSuccess: (response) => {
        setMessage({
          type: 'success',
          text: `🗑️ Successfully cleared all matches and related data!`
        });
        setShowClearWarning(false);
        setClearPreview(null);
        queryClient.invalidateQueries(['admin-matches']);
        queryClient.invalidateQueries(['divisions-overview']);
        queryClient.invalidateQueries(['division-match-status']);
      },
      onError: (error) => {
        setMessage({
          type: 'error',
          text: `❌ Failed to clear matches: ${error.response?.data?.message || error.message}`
        });
        setShowClearWarning(false);
        setClearPreview(null);
      }
    }
  );

  // Handle clear matches button click
  const handleClearMatchesClick = async () => {
    setMessage(null);
    
    if (!hasMatches) {
      setMessage({
        type: 'warning',
        text: '⚠️ No matches exist to clear. Generate matches first.'
      });
      return;
    }
    
    setShowClearWarning(true); // Show dialog immediately
    await getClearPreview();
  };

  // Handle confirm clear
  const handleConfirmClear = () => {
    if (confirmationText !== 'CLEAR ALL') {
      setMessage({
        type: 'error',
        text: '❌ Please type "CLEAR ALL" to confirm deletion'
      });
      return;
    }
    clearMatchesMutation.mutate();
  };

  // Handle cancel clear
  const handleCancelClear = () => {
    setShowClearWarning(false);
    setClearPreview(null);
    setConfirmationText('');
  };

  if (!selectedSeason) {
    return (
      <Container>
        <div>Please select a season to manage match generation.</div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>🎛️ Simple Match Generation Control</Title>
        <Subtitle>
          Basic controls for generating matches (using existing system)
        </Subtitle>
      </Header>

      {message && (
        <MessageContainer $type={message.type}>
          <div style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
            {message.text}
          </div>
        </MessageContainer>
      )}

      <ButtonGrid>
        <ActionButton
          onClick={() => {
            setMessage(null);
            generateMatchesMutation.mutate();
          }}
          $isLoading={generateMatchesMutation.isLoading}
          $variant="primary"
          disabled={readOnly}
        >
          <ButtonIcon>🔧</ButtonIcon>
          <ButtonText>
            {generateMatchesMutation.isLoading ? 'Generating...' : 'Generate All Matches'}
          </ButtonText>
          <ButtonDescription>
            {readOnly ? 'Not available for completed seasons' : 'Create matches for all divisions with 4+ teams'}
          </ButtonDescription>
        </ActionButton>

        <ActionButton
          onClick={handleClearMatchesClick}
          $isLoading={clearMatchesMutation.isLoading}
          $variant="danger"
          $disabled={!hasMatches || readOnly}
        >
          <ButtonIcon>🗑️</ButtonIcon>
          <ButtonText>
            {clearMatchesMutation.isLoading ? 'Clearing...' : 'Clear All Matches'}
          </ButtonText>
          <ButtonDescription>
            {readOnly ? 'Not available for completed seasons' : 'Remove all matches, scores & standings'}
          </ButtonDescription>
        </ActionButton>
        
      </ButtonGrid>

      <InfoSection>
        <strong>📋 Current Status:</strong>
        <br />
        <strong>Matches:</strong> {hasMatches ? (
          <span style={{ color: '#059669' }}>✅ {matchesArray.length || 0} matches generated</span>
        ) : (
          <span style={{ color: '#dc2626' }}>❌ No matches generated</span>
        )}
        <br />
        <strong>Season:</strong> {selectedSeason}
        <br />
        
        {divisionStatusData?.data && (
          <>
            <br />
            <strong>🎯 Last Check Results:</strong>
            <br />
            {divisionStatusData.data.summary.message}
            <br />
            {divisionStatusData.data.divisions && divisionStatusData.data.divisions.length > 0 && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#64748b' }}>
                {divisionStatusData.data.divisions.map((div, index) => (
                  <div key={div.divisionId} style={{ marginBottom: '0.25rem' }}>
                    • <strong>{div.divisionName}</strong>: {div.teamCount} teams 
                    {div.canGenerateMatches ? (
                      <span style={{ color: '#059669' }}> ✅ Ready</span>
                    ) : (
                      <span style={{ color: '#dc2626' }}> ❌ Need {4 - div.teamCount} more teams</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <br />
        <br />
        <strong>💡 Note:</strong> This uses the existing match generation system. 
        Division locking features will be added later.
      </InfoSection>

      {/* Clear Matches Warning Dialog */}
      {showClearWarning && clearPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#dc2626', fontSize: '1.25rem' }}>
              ⚠️ DANGER: Clear All Matches
            </h3>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ margin: '0 0 1rem 0', color: '#374151' }}>
                <strong>This action will permanently delete:</strong>
              </p>
              
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Matches:</span>
                  <strong style={{ color: '#dc2626' }}>{clearPreview.matches}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Score Confirmations:</span>
                  <strong style={{ color: '#dc2626' }}>{clearPreview.scoreConfirmations}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Team Standings:</span>
                  <strong style={{ color: '#dc2626' }}>{clearPreview.teamStandings}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Notifications:</span>
                  <strong style={{ color: '#dc2626' }}>{clearPreview.notifications}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Chat Logs:</span>
                  <strong style={{ color: '#dc2626' }}>{clearPreview.chatLogs || 0}</strong>
                </div>
                <hr style={{ border: 'none', borderTop: '1px solid #fecaca', margin: '0.5rem 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span><strong>Total Records:</strong></span>
                  <strong style={{ color: '#dc2626', fontSize: '1.1rem' }}>{clearPreview.totalRecords}</strong>
                </div>
              </div>

              <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.875rem' }}>
                <strong>This action cannot be undone!</strong> All match data, scores, standings, and notifications will be permanently deleted.
              </p>
              
              <div style={{ marginTop: '1rem', padding: '1rem', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#ea580c' }}>
                  Type <strong>"CLEAR ALL"</strong> to confirm deletion:
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="Type CLEAR ALL here..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #fed7aa',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    background: confirmationText === 'CLEAR ALL' ? '#f0fdf4' : '#ffffff'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancelClear}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmClear}
                disabled={clearMatchesMutation.isLoading || confirmationText !== 'CLEAR ALL'}
                style={{
                  background: (clearMatchesMutation.isLoading || confirmationText !== 'CLEAR ALL') ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: (clearMatchesMutation.isLoading || confirmationText !== 'CLEAR ALL') ? 'not-allowed' : 'pointer',
                  opacity: (clearMatchesMutation.isLoading || confirmationText !== 'CLEAR ALL') ? 0.6 : 1
                }}
              >
                {clearMatchesMutation.isLoading ? 'Clearing...' : confirmationText !== 'CLEAR ALL' ? 'Type "CLEAR ALL" to enable' : 'Yes, Clear Everything'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default SimpleMatchGenerationControl;
